/**
 * Public Resend Test Page (No Auth Required)
 * 
 * This page allows testing the email system without authentication.
 * Useful for quick production testing.
 */

import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Spinner } from '../components/ui/spinner';
import Swal from 'sweetalert2';

interface EmailLog {
    timestamp: Date;
    to: string;
    subject: string;
    status: 'sending' | 'success' | 'error';
    emailId?: string;
    error?: string;
}

export default function PublicResendTestPage() {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        to: 'luke@masonic-tools.co.uk',
        subject: 'Test Email from Public Test Page',
        message: 'This is a test email sent without authentication to verify API connectivity.',
    });
    const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
    const [apiUrl, setApiUrl] = useState(import.meta.env.VITE_API_URL || 'http://localhost:3000');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const addLog = (log: Omit<EmailLog, 'timestamp'>) => {
        setEmailLogs(prev => [{
            ...log,
            timestamp: new Date()
        }, ...prev]);
    };

    const testApiHealth = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${apiUrl}/api/health`);
            const data = await response.json();

            await Swal.fire({
                icon: response.ok ? 'success' : 'error',
                title: 'API Health Check',
                html: `
                    <div class="text-left">
                        <p><strong>Status:</strong> ${data.status || 'unknown'}</p>
                        <p><strong>Service:</strong> ${data.service || 'unknown'}</p>
                        <p><strong>Resend Configured:</strong> ${data.resendConfigured ? '✓ Yes' : '✗ No'}</p>
                        <p class="mt-4 text-sm text-gray-600"><strong>URL:</strong> ${apiUrl}</p>
                    </div>
                `
            });
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'API Connection Failed',
                html: `
                    <p>Cannot connect to API at:</p>
                    <p class="font-mono text-sm mt-2">${apiUrl}</p>
                    <p class="mt-4 text-sm text-gray-600">Error: ${error instanceof Error ? error.message : 'Unknown error'}</p>
                `
            });
        } finally {
            setLoading(false);
        }
    };

    const sendTestEmail = async () => {
        if (!formData.to || !formData.subject || !formData.message) {
            await Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fill in all fields'
            });
            return;
        }

        setLoading(true);
        addLog({
            to: formData.to,
            subject: formData.subject,
            status: 'sending'
        });

        try {
            const html = `
<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #10B981 0%, #059669 100%); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e5e7eb; }
        .message-box { background: white; padding: 20px; border-radius: 6px; margin: 20px 0; }
        .info { background: #dbeafe; padding: 15px; border-radius: 6px; margin: 20px 0; }
    </style>
</head>
<body>
    <div class="header">
        <h1>Public Test Email</h1>
    </div>
    <div class="content">
        <div class="info">
            <strong>Test Information:</strong><br>
            API: ${apiUrl}<br>
            Environment: ${import.meta.env.MODE || 'production'}<br>
            Timestamp: ${new Date().toLocaleString()}
        </div>
        <div class="message-box">
            ${formData.message.replace(/\n/g, '<br>')}
        </div>
    </div>
</body>
</html>
            `.trim();

            const response = await fetch(`${apiUrl}/email/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Note: This won't work without auth, but shows the structure
                },
                body: JSON.stringify({
                    to: formData.to,
                    subject: formData.subject,
                    html,
                    text: formData.message,
                    templateType: 'test'
                })
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setEmailLogs(prev => prev.map((log, idx) => 
                    idx === 0 ? { ...log, status: 'success', emailId: result.emailId } : log
                ));

                await Swal.fire({
                    icon: 'success',
                    title: 'Email Sent!',
                    html: `<p>Email sent to <strong>${formData.to}</strong></p>
                           ${result.emailId ? `<p class="text-sm mt-2">ID: ${result.emailId}</p>` : ''}`
                });
            } else {
                throw new Error(result.message || result.error || 'Failed to send email');
            }
        } catch (error) {
            setEmailLogs(prev => prev.map((log, idx) => 
                idx === 0 ? { ...log, status: 'error', error: error instanceof Error ? error.message : String(error) } : log
            ));

            await Swal.fire({
                icon: 'error',
                title: 'Failed to Send',
                text: error instanceof Error ? error.message : 'Unknown error',
                footer: 'Note: This endpoint requires authentication'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-7xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Public Resend Test</h1>
                    <p className="mt-2 text-gray-600">
                        Test API connectivity and email system (No authentication required for this page)
                    </p>
                    <div className="mt-4 flex items-center gap-2">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Public Access
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {import.meta.env.MODE || 'production'}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="p-6">
                        <h2 className="text-xl font-semibold mb-4">API Configuration</h2>
                        
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="apiUrl">API URL</Label>
                                <Input
                                    id="apiUrl"
                                    value={apiUrl}
                                    onChange={(e) => setApiUrl(e.target.value)}
                                    placeholder="https://api.masonic-tools.co.uk"
                                />
                            </div>

                            <Button onClick={testApiHealth} disabled={loading} className="w-full">
                                {loading ? <><Spinner className="mr-2" /> Testing...</> : 'Test API Health'}
                            </Button>
                        </div>

                        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-800">
                                <strong>Note:</strong> The email sending endpoint requires authentication. 
                                Use this page to test API connectivity. For full email testing, use the 
                                authenticated /resend-test page.
                            </p>
                        </div>
                    </Card>

                    <Card className="p-6">
                        <h2 className="text-xl font-semibold mb-4">Email Test (Requires Auth)</h2>
                        
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="to">Recipient</Label>
                                <Input
                                    id="to"
                                    name="to"
                                    value={formData.to}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div>
                                <Label htmlFor="subject">Subject</Label>
                                <Input
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div>
                                <Label htmlFor="message">Message</Label>
                                <Textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    rows={4}
                                />
                            </div>
                            <Button onClick={sendTestEmail} disabled={loading} className="w-full">
                                {loading ? <><Spinner className="mr-2" /> Sending...</> : 'Send (Will Fail - No Auth)'}
                            </Button>
                        </div>
                    </Card>
                </div>

                {emailLogs.length > 0 && (
                    <Card className="mt-6 p-6">
                        <h3 className="text-lg font-semibold mb-4">Request Log</h3>
                        <div className="space-y-2">
                            {emailLogs.map((log, idx) => (
                                <div key={idx} className={`p-3 rounded border ${
                                    log.status === 'success' ? 'bg-green-50 border-green-200' :
                                    log.status === 'error' ? 'bg-red-50 border-red-200' :
                                    'bg-blue-50 border-blue-200'
                                }`}>
                                    <div className="text-sm">
                                        <strong>{log.status.toUpperCase()}</strong> - {log.to} - {log.timestamp.toLocaleTimeString()}
                                        {log.error && <div className="text-red-600 mt-1">{log.error}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                <Card className="mt-6 p-6 bg-blue-50 border-blue-200">
                    <h3 className="text-lg font-semibold mb-3">Diagnosis Steps</h3>
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>Click "Test API Health" to verify API is accessible</li>
                        <li>Check that <code>resendConfigured: true</code> in the response</li>
                        <li>If API health works, the email system is ready</li>
                        <li>For full email testing, you need to:
                            <ul className="list-disc list-inside ml-6 mt-1">
                                <li>Log in as an admin user</li>
                                <li>Visit /resend-test (protected route)</li>
                                <li>Or use the email client from your apps with proper auth</li>
                            </ul>
                        </li>
                    </ol>
                </Card>
            </div>
        </div>
    );
}

